import { VoicefillLogo } from '@/assets/voicefill-logo';
import { useSettings } from '@/hooks/use-settings';
import { SUPPORTED_LOCALES } from '@/i18n/i18n';
import { useRevenuecat } from '@/lib/revenuecat/provider';
import { useCanRecord } from '@/lib/utils/can-record-check';
import { useHomeUploadStore } from '@/stores/home-upload';
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from '@convex-dev/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Plus, RotateCcw, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { api } from '~/convex/_generated/api';
import { ResponsiveContainer } from '../layout/responsive-container';
import { HomepageListSkeleton } from '../skeletons/homepage-list-skeleton';
import { Button } from '../ui/button';
import { AudioFilePicker, type AudioFilePickerRef } from './audio-file-picker';
import { LanguageFilters } from './language-filters';
import { TemplateButton } from './template-button';

export const TemplateList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const audioFilePickerRef = useRef<AudioFilePickerRef>(null);

  const {
    selectedTemplate,
    setSelectedTemplate,
    isUploading,
    setIsUploading,
    isCreating,
    setIsCreating,
  } = useHomeUploadStore(
    useShallow((s) => ({
      selectedTemplate: s.selectedTemplate,
      setSelectedTemplate: s.setSelectedTemplate,
      isUploading: s.isUploading,
      setIsUploading: s.setIsUploading,
      isCreating: s.isCreating,
      setIsCreating: s.setIsCreating,
    })),
  );

  const { settings } = useSettings();
  const [filterLanguages, setFilterLanguages] = useState<string[]>(() => [
    ...SUPPORTED_LOCALES,
  ]);

  const { mutate: updateSettings } = useMutation({
    mutationFn: useConvexMutation(api.userSettings.updateSettings),
  });

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current && settings) {
      const saved = settings.homepageFilterLanguage;
      setFilterLanguages(
        saved && saved.length > 0 ? saved : [...SUPPORTED_LOCALES],
      );
      hasInitialized.current = true;
    }
  }, [settings]);

  const {
    data: templates,
    isLoading,
    isError,
    refetch,
  } = useQuery(convexQuery(api.templates.list, {}));

  const { mutate: createReport } = useMutation({
    mutationFn: useConvexAction(api.reports.createWithLimitEnforcement),
    onMutate: () => setIsCreating(true),
    onSettled: () => setIsCreating(false),
  });

  const { canRecord, isLoading: isLoadingCanRecord } = useCanRecord();
  const { showPaywall } = useRevenuecat();

  const handleStart = () => {
    if (!canRecord) {
      showPaywall();
      return;
    }

    if (!selectedTemplate) {
      toast.error(t('home:noTemplateSelected'));
      console.error('No template selected');
      return;
    }

    createReport(
      { templateId: selectedTemplate },
      {
        onSuccess: (reportId) => {
          navigate(`/reports/${reportId}/record?autoStart=true`);
        },
        onError: (error) => {
          console.error('Error creating report:', error);
          toast.error(t('home:errorCreatingReport'));
        },
      },
    );
  };

  const filteredTemplates = templates?.filter(
    (template) =>
      filterLanguages.length === 0 ||
      filterLanguages.includes(template.language),
  );

  useEffect(() => {
    if (
      selectedTemplate &&
      filteredTemplates &&
      !filteredTemplates.some((t) => t._id === selectedTemplate)
    ) {
      setSelectedTemplate(null);
    }
  }, [filteredTemplates, selectedTemplate, setSelectedTemplate]);

  const handleLanguageToggle = (lang: string) => {
    const newFilterLanguages = filterLanguages.includes(lang)
      ? filterLanguages.filter((l) => l !== lang)
      : [...filterLanguages, lang];

    setFilterLanguages(newFilterLanguages);

    updateSettings(
      {
        homepageFilterLanguage: newFilterLanguages,
      },
      {
        onError: (error) => {
          console.error('Error toggling language:', error);
          toast.error(t('errors:errorTogglingLanguage'));
        },
      },
    );
  };

  if (isError) {
    return (
      <>
        <ResponsiveContainer
          scrollAwareBorder
          className="flex w-full flex-col items-start gap-3 bg-white py-4 md:pt-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-lg font-bold text-gray-900">
              {t('home:selectTemplate')}
            </h1>
            <p className="text-sm font-light text-gray-500">
              {t('home:selectTemplateDescription')}
            </p>
          </div>
          <LanguageFilters
            filterLanguages={filterLanguages}
            onToggleLanguage={handleLanguageToggle}
          />
        </ResponsiveContainer>
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5">
          <div className="text-xl font-medium">
            {t('home:errorLoadingTemplates')}
          </div>
          <Button variant="default" onClick={() => refetch()}>
            <RotateCcw className="mr-2 size-4" />
            {t('home:retry')}
          </Button>
        </div>
      </>
    );
  }

  const header = (
    <ResponsiveContainer
      scrollAwareBorder
      className="flex w-full flex-col items-start gap-3 bg-white py-4 md:pt-6">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <img
            src="/logo.svg"
            alt=""
            aria-hidden
            className="size-6 shrink-0 select-none"
          />
          <h1 className="text-lg font-bold text-gray-900">
            {t('home:selectTemplate')}
          </h1>
        </div>
        <p className="text-sm font-light text-gray-500">
          {t('home:selectTemplateDescription')}
        </p>
      </div>
      <LanguageFilters
        filterLanguages={filterLanguages}
        onToggleLanguage={handleLanguageToggle}
      />
    </ResponsiveContainer>
  );

  if (templates?.length === 0) {
    return (
      <>
        {header}
        <TemplateListEmpty />
      </>
    );
  }

  return (
    <>
      {header}
      <ResponsiveContainer className="relative flex w-full grow flex-col gap-3 overflow-y-auto pb-3 md:pb-4">
        {isLoading || !templates || isLoadingCanRecord ? (
          <HomepageListSkeleton />
        ) : filteredTemplates?.length === 0 ? (
          <TemplateListLanguageEmpty />
        ) : (
          filteredTemplates?.map((template) => (
            <TemplateButton
              key={template._id}
              template={template}
              isSelected={selectedTemplate === template._id}
              onSelect={() =>
                setSelectedTemplate(
                  selectedTemplate === template._id ? null : template._id,
                )
              }
            />
          ))
        )}
      </ResponsiveContainer>
      <ResponsiveContainer className="w-full md:pb-4">
        <div className="flex w-full gap-2 border-t pt-3 md:pt-4">
          <Button
            className="flex-1"
            onClick={handleStart}
            disabled={!selectedTemplate || isCreating || isUploading}>
            {isCreating ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <VoicefillLogo size={24} />
            )}
            {t('home:start')}
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              if (!canRecord) {
                showPaywall();
                return;
              }
              audioFilePickerRef.current?.openFilePicker();
            }}
            disabled={isCreating || isUploading}>
            {isUploading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Upload className="size-4" />
            )}
            {isUploading
              ? selectedTemplate
                ? t('home:uploadingAndGenerating')
                : t('home:uploading')
              : selectedTemplate
                ? t('home:uploadAndGenerate')
                : t('home:uploadAudio')}
          </Button>
        </div>
        <AudioFilePicker
          ref={audioFilePickerRef}
          templateId={selectedTemplate}
          onUploadStart={() => setIsUploading(true)}
          onUploadComplete={(reportId) => {
            setIsUploading(false);
            navigate(`/reports/${reportId}`);
          }}
          onError={(message) => {
            setIsUploading(false);
            toast.error(message);
          }}
          disabled={isCreating || isUploading}
        />
      </ResponsiveContainer>
    </>
  );
};

const TemplateListEmpty = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <ResponsiveContainer className="relative grow">
      <HomepageListSkeleton pulse={false} />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
        <div className="flex flex-col items-center">
          <p className="text-sm font-medium text-gray-900">
            {t('report:noReports')}
          </p>
          <p className="mt-2 text-sm text-gray-500">
            {t('templates:noTemplatesDescription')}
          </p>
        </div>
        <Button variant="default" onClick={() => navigate('/templates/create')}>
          <Plus className="size-4" />
          {t('home:createTemplate')}
        </Button>
      </div>
    </ResponsiveContainer>
  );
};

const TemplateListLanguageEmpty = () => {
  const { t } = useTranslation();

  return (
    <>
      <HomepageListSkeleton pulse={false} />
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
        <p className="mt-4 text-sm font-medium text-gray-900">
          {t('home:noTemplatesForLanguage')}
        </p>
        <p className="mt-2 text-sm text-gray-500">
          {t('home:noTemplatesForLanguageDescription')}
        </p>
      </div>
    </>
  );
};
